import { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ROUTES, MENU_PAGE_MAPPING, MENU_LABELS } from '../../utils/constants.js';
import { hasMenuAccess } from '../../utils/permissions.js';
import logo from '../../assets/logo.svg';
import DashboardIcon from '../../assets/icons/dashboard.svg?react';
import MapIcon from '../../assets/icons/map.svg?react';
import ChargerIcon from '../../assets/icons/charger.svg?react';
import DrResourceIcon from '../../assets/icons/dr_resource.svg?react';
import StationIcon from '../../assets/icons/station.svg?react';
import DrHistoryIcon from '../../assets/icons/dr-history.svg?react';
import ReportIcon from '../../assets/icons/report.svg?react';
import BrainIcon from '../../assets/icons/brain.svg?react';
import SystemLogIcon from '../../assets/icons/system-log.svg?react';
import SettingIcon from '../../assets/icons/setting.svg?react';
import CloudIcon from '../../assets/icons/cloud.svg?react';
import GroupIcon from '../../assets/icons/group.svg?react';
import CblModelIcon from '../../assets/icons/cbl-model.svg?react';
import ResetIcon from '../../assets/icons/reset.svg?react';
import SmsIcon from '../../assets/icons/sms.svg?react';
import { IoMenu } from "react-icons/io5";
import './Sidebar.css';
import ThemeToggle from '../ThemeToggle/ThemeToggle.jsx';

const Sidebar = ({ collapsed, onToggleSidebar }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [hoveredMenu, setHoveredMenu] = useState(null);
  
  // MENU_PAGE_MAPPING을 사용하여 경로에서 메뉴 식별자를 찾고, MENU_LABELS에서 라벨을 가져오기
  const getMenuLabelFromPath = useMemo(() => {
    return (path) => {
      const menuId = Object.keys(MENU_PAGE_MAPPING).find(key => MENU_PAGE_MAPPING[key] === path);
      return menuId ? MENU_LABELS[menuId] : null;
    };
  }, []);

  const menuItems = useMemo(() => [
    { path: ROUTES.DASHBOARD, label: getMenuLabelFromPath(ROUTES.DASHBOARD) || '대시보드', icon: <DashboardIcon /> },
    { path: ROUTES.MAP_DASHBOARD, label: getMenuLabelFromPath(ROUTES.MAP_DASHBOARD) || '지도 대시보드', icon: <MapIcon /> },
    { path: ROUTES.CHARGER_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.CHARGER_MANAGEMENT) || '충전기 관리', icon: <ChargerIcon /> },
    { path: ROUTES.STATION_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.STATION_MANAGEMENT) || '충전소 관리', icon: <StationIcon /> },
    { path: ROUTES.DR_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.DR_MANAGEMENT) || 'DR 자원 관리', icon: <DrResourceIcon /> },
    { path: ROUTES.DR_HISTORY, label: getMenuLabelFromPath(ROUTES.DR_HISTORY) || 'DR 이력 및 정산', icon: <DrHistoryIcon /> },
    { path: ROUTES.OPM_DATA_LINKAGE_STATUS, label: getMenuLabelFromPath(ROUTES.OPM_DATA_LINKAGE_STATUS) || '고객계약번호 상태 조회', icon: <CloudIcon /> },
    { path: ROUTES.REPORT_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.REPORT_MANAGEMENT) || '보고서 관리', icon: <ReportIcon /> },
    { path: ROUTES.MODULE_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.MODULE_MANAGEMENT) || 'AI 모듈 관리', icon: <BrainIcon /> },
    { path: ROUTES.CBL_MODULE_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.CBL_MODULE_MANAGEMENT) || 'CBL 모듈 관리', icon: <CblModelIcon /> },
    { path: ROUTES.USER_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.USER_MANAGEMENT) || '사용자 및 역할 관리', icon: <GroupIcon /> },
    { path: ROUTES.SYSTEM_LOG, label: getMenuLabelFromPath(ROUTES.SYSTEM_LOG) || '시스템 로그', icon: <SystemLogIcon /> },
    { path: ROUTES.SMS_MANAGEMENT, label: getMenuLabelFromPath(ROUTES.SMS_MANAGEMENT) || 'SMS 발송 관리 및 이력', icon: <SmsIcon /> },
    // { path: ROUTES.SYSTEM_RECOVERY_COMMAND, label: getMenuLabelFromPath(ROUTES.SYSTEM_RECOVERY_COMMAND) || '시스템 복구 명령', icon: <ResetIcon /> },
    // { path: ROUTES.SYSTEM_ALERT_HISTORY, label: '시스템 알림 내역', icon: <AlertHistoryIcon /> },
    { path: ROUTES.SETTINGS, label: getMenuLabelFromPath(ROUTES.SETTINGS) || '시스템 설정', icon: <SettingIcon /> },
    // {
    //   label: '시스템',
    //   icon: <SettingIcon />,
    //   children: [
    //     { path: ROUTES.SETTINGS, label: '설정', icon: <SettingIcon /> },
    //     { path: ROUTES.MESSAGE_MANAGEMENT, label: '메시지 관리', icon: '📧' },
    //     { path: ROUTES.ROLE_MANAGEMENT, label: '역할 관리', icon: '👥' },
    //     { path: ROUTES.USER_MANAGEMENT, label: '사용자 권한 설정', icon: '👤' },
    //   ]
    // },
  ], [getMenuLabelFromPath]);

  // 메뉴 토글 함수
  const toggleMenu = (menuLabel) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuLabel)) {
      newExpanded.delete(menuLabel);
    } else {
      newExpanded.add(menuLabel);
    }
    setExpandedMenus(newExpanded);
  };

  // 현재 활성 메뉴 확인
  const isMenuActive = (item) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return location.pathname === item.path;
  };

  // 현재 페이지에 해당하는 부모 메뉴 자동 확장
  useEffect(() => {
    const newExpanded = new Set(expandedMenus);
    let hasChanges = false;

    menuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => location.pathname === child.path);
        if (hasActiveChild && !newExpanded.has(item.label)) {
          newExpanded.add(item.label);
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setExpandedMenus(newExpanded);
    }
  }, [location.pathname]);

  // 사용자 권한에 따라 메뉴 필터링
  const filterMenuItems = (items) => {
    return items.filter(item => {
      if (item.children) {
        // 하위 메뉴가 있는 경우, 접근 가능한 하위 메뉴가 하나라도 있으면 표시
        const filteredChildren = item.children.filter(child => 
          hasMenuAccess(child.label)
        );
        return filteredChildren.length > 0;
      }
      return hasMenuAccess(item.label);
    });
  };

  const filteredMenuItems = filterMenuItems(menuItems);

    // 메뉴 아이템 렌더링
  const renderMenuItem = (item, index) => {
    if (item.children) {
      // 하위 메뉴가 있는 경우
      const filteredChildren = item.children.filter(child => 
        hasMenuAccess(child.label)
      );
      
      if (filteredChildren.length === 0) return null;

      const isExpanded = expandedMenus.has(item.label);
      const isActive = isMenuActive(item);
      const isHovered = hoveredMenu === item.label;

      return (
        <>
          <li 
            key={`${item.label}-${index}`} 
            className="nav-item has-children"
            onMouseEnter={() => setHoveredMenu(item.label)}
            onMouseLeave={() => setHoveredMenu(null)}
          >
            <button
              className={`nav-link flex align-center transition ${isActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
              onClick={() => toggleMenu(item.label)}
              title={collapsed ? item.label : ''}
            >
              <span className="nav-icon text-center">{item.icon}</span>
              {!collapsed && (
                <>
                  <span className="nav-label text-left">{item.label}</span>
                  <span className={`nav-arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
                </>
              )}
            </button>
            
            {/* 축소 모드에서 호버 시 하위 메뉴 표시 */}
            {collapsed && isHovered && (
              <div className="collapsed-submenu">
                <div className="submenu-header">{item.label}</div>
                <ul className="submenu-list">
                  {filteredChildren.map((child, childIndex) => (
                    <li key={`${child.path}-${childIndex}`} className="submenu-item">
                      <NavLink
                        to={child.path}
                        className={({ isActive }) => 
                          `submenu-link ${isActive ? 'active' : ''}`
                        }
                        title={child.label}
                      >
                        <span className="submenu-icon">{child.icon}</span>
                        <span className="submenu-label">{child.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
          
          {/* 확장 모드에서 하위 메뉴 표시 */}
          {!collapsed && isExpanded && (
            <ul className="sub-menu">
              {filteredChildren.map((child, childIndex) => (
                <li key={`${child.path}-${childIndex}`} className="sub-nav-item">
                  <NavLink
                    to={child.path}
                    className={({ isActive }) => 
                      `sub-nav-link flex align-start transition ${isActive ? 'active' : ''}`
                    }
                  >
                    <span className="sub-nav-icon text-center">{child.icon}</span>
                    <span className="sub-nav-label">{child.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </>
      );
    } else {
      // 일반 메뉴 아이템
      return (
        <li key={`${item.path}-${index}`} className="nav-item">
          <NavLink
            to={item.path}
            className={({ isActive }) => 
              `nav-link transition ${isActive ? 'active' : ''}`
            }
            title={collapsed ? item.label : ''}
          >
            {item.icon}
            {!collapsed && <span className="nav-label text-left text-b1-400 text-primary-600 pl-5">{item.label}</span>}
          </NavLink>
        </li>
      );
    }
  };

  return (
    <aside className={`app-sidebar flex flex-col ${collapsed ? 'collapsed' : ''}`}>
      <button
        className={`position-absolute sidebar-toggle cursor-pointer ${collapsed ? 'collapsed' : ''}`}
        onClick={onToggleSidebar}
        aria-label="사이드바 토글"
      >
        <IoMenu />
      </button>
      <div className="sidebar-header flex flex-col align-center justify-center">
        <img src={logo} alt="logo" className={`sidebar-logo ${collapsed ? 'collapsed' : ''}`} />
        {
          !collapsed && (
            <>
              <h2 className="text-h2 text-gray-800 mt-10">
              한국동서발전
              </h2>
              <p className="text-b1-700 text-gray-800">
                전기차 충전모니터링 시스템
              </p>
            </>
          )
        }
        
      </div>
      <nav className={`sidebar-nav flex-1 ${collapsed ? 'collapsed' : ''}`}>
        <ul className="nav-menu flex flex-col">
          {filteredMenuItems.map((item, index) => renderMenuItem(item, index))}
        </ul>
        <div className="flex align-center justify-center py-10">
          <ThemeToggle collapsed={collapsed} />
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar; 